// Utility functions for URL query parameter management and filter synchronization
import { getConversationAdvancedFilterDefaults } from "./conversationFilterModel";

// Helper function to check if a value should be included in URL
const shouldIncludeInUrl = (key, value) => {
  // Handle null, undefined, empty string
  if (value === null || value === undefined || value === '') {
    return false;
  }
  
  // Special handling for array fields that should not appear in URL when empty
  if ((key === 'agent_feedback_multiple' || key === 'business_insights_multiple') && Array.isArray(value)) {
    // Only include if array has meaningful content
    return value.length > 0 && value.some(item => {
      if (key === 'agent_feedback_multiple') {
        return item.agent_feedback_parameter || 
               item.agent_feedback_parameter_score_lower !== null || 
               item.agent_feedback_parameter_score_upper !== null;
      }
      if (key === 'business_insights_multiple') {
        return item.business_insights_parameter || 
               item.business_insights_parameter_value !== null;
      }
      return true;
    });
  }
  
  // For other arrays, exclude if empty
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  
  // For objects, exclude if empty
  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }
  
  return true;
};

const isModuleParameterFilterKey = (key) => {
  if (typeof key !== 'string') return false;
  return key.startsWith('agent_feedback_') || key.startsWith('business_insight_') || key.startsWith('custom_module_');
};

export const extractModuleParameterFiltersFromFlatFilters = (flatFilters = {}) => {
  const result = [];
  const betweenMap = {};

  Object.entries(flatFilters).forEach(([key, value]) => {
    if (!isModuleParameterFilterKey(key) || value === null || value === undefined || value === '') return;

    if (key.startsWith('agent_feedback_') && (key.endsWith('_score_lower') || key.endsWith('_score_upper'))) {
      const suffix = key.endsWith('_score_lower') ? '_score_lower' : '_score_upper';
      const base = key.slice(0, -suffix.length);
      const field = base.replace(/^agent_feedback_/, '');
      if (!field) return;
      if (!betweenMap[base]) {
        betweenMap[base] = { field, operator: 'between', value: {}, module: 'agent_feedback' };
      }
      if (suffix === '_score_lower') betweenMap[base].value.lower = value;
      if (suffix === '_score_upper') betweenMap[base].value.upper = value;
      return;
    }

    if ((key.startsWith('business_insight_') || key.startsWith('custom_module_')) && (key.endsWith('_lower') || key.endsWith('_upper'))) {
      const modulePrefix = key.startsWith('business_insight_') ? 'business_insight_' : 'custom_module_';
      const moduleName = key.startsWith('business_insight_') ? 'business_insight' : 'custom_module';
      const suffix = key.endsWith('_lower') ? '_lower' : '_upper';
      const base = key.slice(0, -suffix.length);
      const field = base.replace(new RegExp(`^${modulePrefix}`), '');
      if (!field) return;
      const mapKey = `${moduleName}:${field}`;
      if (!betweenMap[mapKey]) {
        betweenMap[mapKey] = { field, operator: 'between', value: {}, module: moduleName };
      }
      if (suffix === '_lower') betweenMap[mapKey].value.lower = value;
      if (suffix === '_upper') betweenMap[mapKey].value.upper = value;
      return;
    }

    if (key.startsWith('business_insight_')) {
      const field = key.replace(/^business_insight_/, '');
      if (!field) return;
      result.push({
        field,
        operator: Array.isArray(value) ? 'in' : 'eq',
        value,
        module: 'business_insight'
      });
      return;
    }

    if (key.startsWith('custom_module_')) {
      const field = key.replace(/^custom_module_/, '');
      if (!field) return;
      result.push({
        field,
        operator: Array.isArray(value) ? 'in' : 'eq',
        value,
        module: 'custom_module'
      });
    }
  });

  Object.values(betweenMap).forEach((item) => {
    const lower = item.value?.lower;
    const upper = item.value?.upper;
    if ((lower !== undefined && lower !== null && lower !== '') || (upper !== undefined && upper !== null && upper !== '')) {
      result.push(item);
    }
  });

  return result;
};

// Serialize filters to URL query parameters
export const serializeFiltersToQuery = (filters, advancedFilters = {}, customStart = null, customEnd = null) => {
  const params = new URLSearchParams();
  
  // Handle basic filters
  Object.entries(filters).forEach(([key, value]) => {
    if (shouldIncludeInUrl(key, value)) {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== null && item !== undefined && item !== '') {
            params.append(key, item);
          }
        });
      } else {
        params.set(key, value);
      }
    }
  });
  
  // Handle custom date ranges
  if (customStart) params.set('customStart', customStart);
  if (customEnd) params.set('customEnd', customEnd);
  
  // Handle advanced filters (drop orphan CRM date bounds when preset cleared)
  let normalizedAdvanced = sanitizeCrmLeadCreatedAtAdvancedFilters(advancedFilters);
  if (!isCrmLeadCreatedAtFilterActive(normalizedAdvanced)) {
    normalizedAdvanced = { ...normalizedAdvanced };
    CRM_LEAD_CREATED_AT_FILTER_KEYS.forEach((key) => {
      delete normalizedAdvanced[key];
    });
  }
  Object.entries(normalizedAdvanced).forEach(([key, value]) => {
    if (shouldIncludeInUrl(key, value)) {
      // For complex objects like arrays, serialize as JSON
      if (typeof value === 'object') {
        params.set(`adv_${key}`, JSON.stringify(value));
      } else {
        params.set(`adv_${key}`, value);
      }
    }
  });
  
  return params.toString();
};

/** Drop legacy single-range keys; map URL `customStart`/`customEnd` onto last-interaction fields when needed. */
export const normalizeInteractionFiltersFromUrl = (filters = {}, { customStart = '', customEnd = '' } = {}) => {
  const next = { ...filters };
  if (next.time && !next.lastinteraction) {
    next.lastinteraction = next.time;
  }
  delete next.time;
  const lastIsCustom = String(next.lastinteraction || "").toLowerCase().includes("custom");
  const firstIsCustom = String(next.firstinteraction || "").toLowerCase().includes("custom");
  if (customStart && lastIsCustom && !next["Last Interaction Start"]) {
    next["Last Interaction Start"] = customStart;
  }
  if (customEnd && lastIsCustom && !next["Last Interaction End"]) {
    next["Last Interaction End"] = customEnd;
  }
  if (customStart && firstIsCustom && !next["First Interaction Start"] && !lastIsCustom) {
    next["First Interaction Start"] = customStart;
  }
  if (customEnd && firstIsCustom && !next["First Interaction End"] && !lastIsCustom) {
    next["First Interaction End"] = customEnd;
  }
  delete next.customStart;
  delete next.customEnd;
  return next;
};

/** CRM lead created-at filter keys kept in sync for state + URL. */
export const CRM_LEAD_CREATED_AT_FILTER_KEYS = [
  "crm_lead_created_at",
  "crm_lead_created_at_start",
  "crm_lead_created_at_end",
  "crm_lead_created_at_range_type",
];

/** Hide raw CRM keys in generic advanced-filter chip loops. */
export const shouldHideCrmLeadCreatedAtChipKey = (key) =>
  CRM_LEAD_CREATED_AT_FILTER_KEYS.includes(key);

/** True when page schema includes CRM lead created-at (Conversation Analytics only). */
export const pageSupportsCrmLeadCreatedAtFilter = (defaultAdvancedFilters = {}) =>
  CRM_LEAD_CREATED_AT_FILTER_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(defaultAdvancedFilters, key)
  );

export const stripCrmLeadCreatedAtFromAdvancedFilters = (advancedFilters = {}) => {
  const next = { ...advancedFilters };
  CRM_LEAD_CREATED_AT_FILTER_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
};

export const clearCrmLeadCreatedAtFields = (state = {}) => ({
  ...state,
  crm_lead_created_at: "",
  crm_lead_created_at_start: "",
  crm_lead_created_at_end: "",
  crm_lead_created_at_range_type: "between",
});

export const isCrmLeadCreatedAtFilterActive = (advancedFilters = {}) =>
  Boolean(
    String(advancedFilters.crm_lead_created_at || "").trim() ||
      String(advancedFilters.crm_lead_created_at_start || "").trim() ||
      String(advancedFilters.crm_lead_created_at_end || "").trim()
  );

export const formatCrmLeadCreatedAtChipLabel = (advancedFilters = {}) => {
  const preset = String(advancedFilters.crm_lead_created_at || "").trim();
  const start = String(advancedFilters.crm_lead_created_at_start || "").trim();
  const end = String(advancedFilters.crm_lead_created_at_end || "").trim();
  const rangeType = String(advancedFilters.crm_lead_created_at_range_type || "").toLowerCase();

  if (preset && !preset.toLowerCase().includes("custom")) {
    return `CRM Lead Created At - ${preset}`;
  }
  if (!start) return "";
  if (rangeType === "after" || (start && !end)) {
    return `CRM Lead Created At - ${start} onwards`;
  }
  if (start && end) {
    return `CRM Lead Created At - ${start} to ${end}`;
  }
  return "";
};

/** Update CRM preset in panel/page state (keeps Custom Range open while dates are picked). */
export const setCrmLeadCreatedAtPreset = (prev = {}, selected = "") => {
  if (!selected) {
    return clearCrmLeadCreatedAtFields(prev);
  }
  if (!String(selected).toLowerCase().includes("custom")) {
    return {
      ...prev,
      crm_lead_created_at: selected,
      crm_lead_created_at_start: "",
      crm_lead_created_at_end: "",
      crm_lead_created_at_range_type: "between",
    };
  }
  return {
    ...prev,
    crm_lead_created_at: selected,
    crm_lead_created_at_range_type: prev.crm_lead_created_at_range_type || "between",
  };
};

/** Normalize CRM fields for state/URL (allows Custom Range without dates while editing). */
export const sanitizeCrmLeadCreatedAtAdvancedFilters = (advancedFilters = {}) => {
  const next = { ...advancedFilters };
  const preset = String(next.crm_lead_created_at || "").trim();
  const start = String(next.crm_lead_created_at_start || "").trim();
  const end = String(next.crm_lead_created_at_end || "").trim();

  if (!preset && (start || end)) {
    return clearCrmLeadCreatedAtFields(next);
  }
  if (!preset) {
    return clearCrmLeadCreatedAtFields(next);
  }
  if (!preset.toLowerCase().includes("custom")) {
    return {
      ...next,
      crm_lead_created_at: preset,
      crm_lead_created_at_start: "",
      crm_lead_created_at_end: "",
      crm_lead_created_at_range_type: "between",
    };
  }
  if (!start && !end) {
    return {
      ...next,
      crm_lead_created_at: preset,
      crm_lead_created_at_start: "",
      crm_lead_created_at_end: "",
      crm_lead_created_at_range_type: next.crm_lead_created_at_range_type || "between",
    };
  }
  const rangeType = String(next.crm_lead_created_at_range_type || "").toLowerCase();
  if (rangeType === "after" || (start && !end)) {
    return {
      ...next,
      crm_lead_created_at_end: "",
      crm_lead_created_at_range_type: "after",
    };
  }
  return next;
};

/** On Apply: drop incomplete Custom Range (no start date). */
export const sanitizeCrmLeadCreatedAtForApply = (advancedFilters = {}) => {
  const next = sanitizeCrmLeadCreatedAtAdvancedFilters(advancedFilters);
  const preset = String(next.crm_lead_created_at || "").trim();
  if (preset.toLowerCase().includes("custom") && !String(next.crm_lead_created_at_start || "").trim()) {
    return clearCrmLeadCreatedAtFields(next);
  }
  return next;
};

export const clearAdvancedFilterField = (prev, key) => {
  if (CRM_LEAD_CREATED_AT_FILTER_KEYS.includes(key)) {
    return clearCrmLeadCreatedAtFields(prev);
  }
  return { ...prev, [key]: "" };
};

/** Logged-in dashboard user email from cookie (sync, safe for API calls on first paint). */
export const getDashboardUserEmail = () => {
  try {
    if (typeof document === "undefined") return null;
    const match = document.cookie.match(/(?:^|;\s*)dashboard_login_user_creds=([^;]*)/);
    if (!match?.[1]) return null;
    const user = JSON.parse(decodeURIComponent(match[1]));
    return user?.email || null;
  } catch {
    return null;
  }
};

export const OVERALL_SCORE_DISTRIBUTION_BUCKETS = {
  Poor: { lower: 0, upper: 50 },
  Average: { lower: 51, upper: 75 },
  Good: { lower: 76, upper: 90 },
  Excellent: { lower: 91, upper: 100 },
};

export const getOverallScoreRangeForDistributionLabel = (label) => {
  if (!label) return null;
  return OVERALL_SCORE_DISTRIBUTION_BUCKETS[String(label).trim()] || null;
};

const CALLS_DRILLDOWN_ADVANCED_KEYS = Object.keys(getConversationAdvancedFilterDefaults())
  .filter((key) => !key.endsWith("_multiple"));

/** Parent filters for ConversationRecords modal drilldown (no flat module query keys). */
export const buildCallsPageDrilldownFilterApplied = (
  filters = {},
  advancedFilters = {},
  { customStart, customEnd } = {}
) => {
  const merged = {
    ...filters,
    ...advancedFilters,
    customStart: customStart ?? filters.customStart ?? "",
    customEnd: customEnd ?? filters.customEnd ?? "",
    customRangeType: filters.customRangeType || advancedFilters.customRangeType || "between",
  };

  Object.keys(merged).forEach((key) => {
    if (!isModuleParameterFilterKey(key)) return;
    if (
      key === "custom_module_parameter_name" ||
      key === "custom_module_parameter_answer" ||
      key.endsWith("_multiple")
    ) {
      return;
    }
    delete merged[key];
  });

  return merged;
};

export const getConversationSegmentAdvancedFilterPatch = (segment = {}) => {
  const patch = {};
  const { id, name, filter_applied: filterApplied = {} } = segment;

  if (id === "Call Distribution" && name) {
    const range = getOverallScoreRangeForDistributionLabel(name);
    if (range) {
      patch.scoreLower = range.lower;
      patch.scoreUpper = range.upper;
    }
    // Display-only chip label (API still uses scoreLower/scoreUpper)
    patch["Call Distribution"] = name;
  }
  if (id === "fatalCalls" || id === "fatal") patch.fatal = "Yes";
  if (id === "needsReview") patch.needsReview = "Yes";

  CALLS_DRILLDOWN_ADVANCED_KEYS.forEach((key) => {
    const value = filterApplied[key];
    if (value === null || value === undefined || value === "") return;
    if (Array.isArray(value) && value.length === 0) return;
    patch[key] = value;
  });

  if (Array.isArray(filterApplied.agent_feedback_multiple) && filterApplied.agent_feedback_multiple.length > 0) {
    patch.agent_feedback_multiple = filterApplied.agent_feedback_multiple;
  }
  if (Array.isArray(filterApplied.business_insights_multiple) && filterApplied.business_insights_multiple.length > 0) {
    patch.business_insights_multiple = filterApplied.business_insights_multiple;
  }
  if (Array.isArray(filterApplied.custom_module_multiple) && filterApplied.custom_module_multiple.length > 0) {
    patch.custom_module_multiple = filterApplied.custom_module_multiple;
  }

  return patch;
};

export const shouldUseGenericSegmentAdvancedKey = (segment = {}) => {
  const id = segment?.id;
  if (!id) return false;
  if (
    id === "Call Distribution" ||
    id === "fatalCalls" ||
    id === "fatal" ||
    id === "needsReview" ||
    id === "calls" ||
    id === "chats"
  ) {
    return false;
  }
  if (segment.type === "agent_feedback_call_distribution") return false;
  return true;
};

/** One-shot filters/advancedFilters for ConversationRecords segment modal. */
export const buildConversationRecordsStateFromSegment = (
  segment,
  defaultFilters = {},
  defaultAdvancedFilters = {}
) => {
  const filterApplied = segment?.filter_applied || {};
  const filters = { ...defaultFilters };
  const advancedFilters = { ...defaultAdvancedFilters };

  if (filterApplied.time) filters.time = filterApplied.time;
  if (filterApplied.employee) filters.employee = filterApplied.employee;
  if (filterApplied.team_name || filterApplied.team) {
    filters.team_name = filterApplied.team_name || filterApplied.team;
  }
  if (filterApplied.record_type) filters.record_type = filterApplied.record_type;
  if (filterApplied.conversationId) filters.conversationId = filterApplied.conversationId;
  if (filterApplied.customStart) filters.customStart = filterApplied.customStart;
  if (filterApplied.customEnd !== undefined && filterApplied.customEnd !== null) {
    filters.customEnd = filterApplied.customEnd;
  }
  const rangeType = String(
    filterApplied.customRangeType || filterApplied.custom_range_type || ""
  ).toLowerCase();
  if (rangeType === "after" || rangeType === "between") {
    filters.customRangeType = rangeType;
  }

  const disallowedModuleShapeKeys = new Set([
    "agent_feedback_multiple",
    "business_insights_multiple",
    "custom_module_multiple",
    "custom_module_parameter_name",
    "custom_module_parameter_answer",
  ]);

  Object.entries(filterApplied).forEach(([key, value]) => {
    if (!isModuleParameterFilterKey(key)) return;
    if (disallowedModuleShapeKeys.has(key) || key.endsWith("_multiple")) return;
    if (value === null || value === undefined || value === "") return;
    if (Array.isArray(value) && value.some((item) => item && typeof item === "object")) return;
    filters[key] = value;
  });

  Object.assign(
    advancedFilters,
    getConversationSegmentAdvancedFilterPatch({ ...segment, filter_applied: filterApplied })
  );

  if (filterApplied.audioduration) advancedFilters.audioDuration = filterApplied.audioduration;
  if (filterApplied.audioDuration) advancedFilters.audioDuration = filterApplied.audioDuration;
  if (filterApplied.audioDurationLower != null && filterApplied.audioDurationLower !== "") {
    advancedFilters.audioDurationLower = filterApplied.audioDurationLower;
  }
  if (filterApplied.audioDurationUpper != null && filterApplied.audioDurationUpper !== "") {
    advancedFilters.audioDurationUpper = filterApplied.audioDurationUpper;
  }

  const crmPatch = crmLeadCreatedAtPatchFromSegmentFilter(filterApplied);
  if (crmPatch) Object.assign(advancedFilters, crmPatch);

  if (filterApplied.agentFeedbackParameter && filterApplied.agentFeedbackParameterScoreLower != null && filterApplied.agentFeedbackParameterScoreUpper != null) {
    advancedFilters.agent_feedback_multiple = [
      {
        agent_feedback_parameter: filterApplied.agentFeedbackParameter,
        agent_feedback_parameter_score_lower: filterApplied.agentFeedbackParameterScoreLower,
        agent_feedback_parameter_score_upper: filterApplied.agentFeedbackParameterScoreUpper,
      },
    ];
  }
  if (Array.isArray(filterApplied.agent_feedback_multiple) && filterApplied.agent_feedback_multiple.length > 0) {
    advancedFilters.agent_feedback_multiple = filterApplied.agent_feedback_multiple;
  }
  if (filterApplied.businessInsightParameter && filterApplied.businessInsightValue) {
    advancedFilters.business_insights_multiple = [
      {
        business_insights_parameter: filterApplied.businessInsightParameter,
        business_insights_parameter_value: filterApplied.businessInsightValue,
      },
    ];
  }
  if (Array.isArray(filterApplied.business_insights_multiple) && filterApplied.business_insights_multiple.length > 0) {
    advancedFilters.business_insights_multiple = filterApplied.business_insights_multiple;
  }

  if (segment?.id === "chats") filters.record_type = "chat";
  if (segment?.id === "calls") filters.record_type = "call";

  if (segment?.id && segment?.name && shouldUseGenericSegmentAdvancedKey(segment)) {
    advancedFilters[segment.id] = segment.name;
  }

  return { filters, advancedFilters };
};

/** Build advanced-filter patch for CRM lead created-at from drilldown `filter_applied`. */
export const crmLeadCreatedAtPatchFromSegmentFilter = (filterApplied = {}) => {
  const preset = String(filterApplied.crm_lead_created_at || "").trim();
  if (!preset) return null;
  if (!preset.toLowerCase().includes("custom")) {
    return {
      crm_lead_created_at: preset,
      crm_lead_created_at_start: "",
      crm_lead_created_at_end: "",
      crm_lead_created_at_range_type: "between",
    };
  }
  return sanitizeCrmLeadCreatedAtAdvancedFilters({
    crm_lead_created_at: preset,
    crm_lead_created_at_start: filterApplied.crm_lead_created_at_start || "",
    crm_lead_created_at_end: filterApplied.crm_lead_created_at_end || "",
    crm_lead_created_at_range_type: filterApplied.crm_lead_created_at_range_type || "between",
  });
};

/** Apply CRM lead created-at from chart/modal drilldown `segment.filter_applied`. */
export const applyCrmLeadCreatedAtFromSegmentFilter = (filterApplied = {}, setAdvancedFilters) => {
  const patch = crmLeadCreatedAtPatchFromSegmentFilter(filterApplied);
  if (!patch) return;
  setAdvancedFilters((prev) => ({ ...prev, ...patch }));
};

/** CRM lead created-at args for dashboard API calls (skips incomplete Custom Range). */
export const getCrmLeadCreatedAtApiArgs = (advancedFilters = {}) => {
  const preset = String(advancedFilters.crm_lead_created_at || "").trim();
  if (!preset) {
    return ["", "", "", "between"];
  }
  if (!preset.toLowerCase().includes("custom")) {
    return [preset, "", "", "between"];
  }
  const start = String(advancedFilters.crm_lead_created_at_start || "").trim();
  const end = String(advancedFilters.crm_lead_created_at_end || "").trim();
  if (!start) {
    return ["", "", "", "between"];
  }
  const rangeType = String(advancedFilters.crm_lead_created_at_range_type || "between").toLowerCase();
  if (rangeType === "after" || (start && !end)) {
    return [preset, start, "", "after"];
  }
  return [preset, start, end, "between"];
};

// Parse URL query parameters to filter objects
export const parseQueryToFilters = (searchParams) => {
  const filters = {};
  const advancedFilters = {};
  let customStart = '';
  let customEnd = '';
    
  for (const [key, value] of searchParams.entries()) {
    // Treat '+' as space for application/x-www-form-urlencoded semantics, then decode percent-escapes
    const decodedValue = decodeURIComponent(value.replace(/\+/g, ' '));
    
    if (key === 'customStart') {
      customStart = decodedValue;
    } else if (key === 'customEnd') {
      customEnd = decodedValue;
    } else if (key.startsWith('adv_')) {
      // Advanced filter
      const advKey = key.replace('adv_', '');
      try {
        // Try to parse JSON for complex filters
        advancedFilters[advKey] = JSON.parse(decodedValue);
      } catch {
        // If not JSON, treat as string
        advancedFilters[advKey] = decodedValue;
      }
    } else {
      // Basic filter
      if (filters[key] === undefined) {
        filters[key] = decodedValue;
      } else if (Array.isArray(filters[key])) {
        filters[key].push(decodedValue);
      } else {
        filters[key] = [filters[key], decodedValue];
      }
    }
  }
  
  
  return { filters, advancedFilters, customStart, customEnd };
};

// Initialize filters from URL on component mount
export const initializeFiltersFromUrl = (location, defaultFilters, defaultAdvancedFilters = {}) => {
  const searchParams = new URLSearchParams(location.search);
  const { filters: urlFilters, advancedFilters: urlAdvancedFilters, customStart, customEnd } = parseQueryToFilters(searchParams);
  
  // Debug logging for new tab issue
  if (location.search) {
  }
  
  // Merge URL filters with defaults, but only keep filters that exist in defaults
  const mergedFilters = { ...defaultFilters };
  const mergedAdvancedFilters = { ...defaultAdvancedFilters };
  
  // Only apply URL filters that are actually used by this component
  Object.keys(defaultFilters).forEach(key => {
    if (urlFilters[key] !== undefined) {
      mergedFilters[key] = urlFilters[key];
    }
  });
  // Preserve split module-parameter URL filters in base filter state.
  Object.keys(urlFilters).forEach((key) => {
    if (!(key in defaultFilters) && isModuleParameterFilterKey(key)) {
      mergedFilters[key] = urlFilters[key];
    }
  });

  // Ensure customStart/customEnd from URL are reflected in the returned filters object
  if (customStart) mergedFilters.customStart = customStart;
  if (customEnd) mergedFilters.customEnd = customEnd;
  
  Object.keys(defaultAdvancedFilters).forEach(key => {
    if (urlAdvancedFilters[key] !== undefined) {
      mergedAdvancedFilters[key] = urlAdvancedFilters[key];
    }
  });
  if (pageSupportsCrmLeadCreatedAtFilter(defaultAdvancedFilters)) {
    Object.assign(mergedAdvancedFilters, sanitizeCrmLeadCreatedAtAdvancedFilters(mergedAdvancedFilters));
  } else {
    CRM_LEAD_CREATED_AT_FILTER_KEYS.forEach((key) => {
      delete mergedAdvancedFilters[key];
    });
  }

  // Bridge audio-duration fields between pages that use different filter schemas:
  // - quick filters pages: filters.audioduration + filters.audioDurationLower/Upper
  // - conversations page: advancedFilters.audioDuration + advancedFilters.audioDurationLower/Upper
  const parseAudioRange = (value) => {
    if (typeof value !== 'string') return null;
    const match = value.match(/^(\d+)\s*-\s*(\d+)$/);
    if (!match) return null;
    return { lower: Number(match[1]), upper: Number(match[2]) };
  };

  const hasQuickAudioSchema = Object.prototype.hasOwnProperty.call(defaultFilters, 'audioduration');
  const hasAdvancedAudioSchema = Object.prototype.hasOwnProperty.call(defaultAdvancedFilters, 'audioDuration');

  if (hasAdvancedAudioSchema) {
    const fromQuickAudio = urlFilters.audioduration;
    const lowerFromQuick = urlFilters.audioDurationLower;
    const upperFromQuick = urlFilters.audioDurationUpper;
    const hasQuickLower = lowerFromQuick !== undefined && lowerFromQuick !== null && lowerFromQuick !== '';
    const hasQuickUpper = upperFromQuick !== undefined && upperFromQuick !== null && upperFromQuick !== '';
    if (fromQuickAudio !== undefined) {
      mergedAdvancedFilters.audioDuration = fromQuickAudio;
      if (fromQuickAudio === 'Custom Range' && (hasQuickLower || hasQuickUpper)) {
        if (Object.prototype.hasOwnProperty.call(defaultAdvancedFilters, 'audioDurationLower') && hasQuickLower) {
          mergedAdvancedFilters.audioDurationLower = Number(lowerFromQuick);
        }
        if (Object.prototype.hasOwnProperty.call(defaultAdvancedFilters, 'audioDurationUpper') && hasQuickUpper) {
          mergedAdvancedFilters.audioDurationUpper = Number(upperFromQuick);
        }
      }
      const parsedRange = parseAudioRange(fromQuickAudio);
      if (parsedRange) {
        mergedAdvancedFilters.audioDuration = 'Custom Range';
        if (Object.prototype.hasOwnProperty.call(defaultAdvancedFilters, 'audioDurationLower')) {
          mergedAdvancedFilters.audioDurationLower = parsedRange.lower;
        }
        if (Object.prototype.hasOwnProperty.call(defaultAdvancedFilters, 'audioDurationUpper')) {
          mergedAdvancedFilters.audioDurationUpper = parsedRange.upper;
        }
      }
    } else if (hasQuickLower || hasQuickUpper) {
      // Support URLs carrying only split bounds without audioduration key.
      mergedAdvancedFilters.audioDuration = 'Custom Range';
      if (Object.prototype.hasOwnProperty.call(defaultAdvancedFilters, 'audioDurationLower') && hasQuickLower) {
        mergedAdvancedFilters.audioDurationLower = Number(lowerFromQuick);
      }
      if (Object.prototype.hasOwnProperty.call(defaultAdvancedFilters, 'audioDurationUpper') && hasQuickUpper) {
        mergedAdvancedFilters.audioDurationUpper = Number(upperFromQuick);
      }
    }
  }

  if (hasQuickAudioSchema) {
    const fromAdvancedAudio = urlAdvancedFilters.audioDuration;
    if (fromAdvancedAudio !== undefined) {
      mergedFilters.audioduration = fromAdvancedAudio;
      if (fromAdvancedAudio === 'Custom Range') {
        const lowerFromAdv = urlAdvancedFilters.audioDurationLower;
        const upperFromAdv = urlAdvancedFilters.audioDurationUpper;
        if (Object.prototype.hasOwnProperty.call(defaultFilters, 'audioDurationLower') && lowerFromAdv !== undefined && lowerFromAdv !== null && lowerFromAdv !== '') {
          mergedFilters.audioDurationLower = String(lowerFromAdv);
        }
        if (Object.prototype.hasOwnProperty.call(defaultFilters, 'audioDurationUpper') && upperFromAdv !== undefined && upperFromAdv !== null && upperFromAdv !== '') {
          mergedFilters.audioDurationUpper = String(upperFromAdv);
        }
      } else {
        const parsedRange = parseAudioRange(fromAdvancedAudio);
        if (parsedRange) {
          mergedFilters.audioduration = 'Custom Range';
          if (Object.prototype.hasOwnProperty.call(defaultFilters, 'audioDurationLower')) {
            mergedFilters.audioDurationLower = String(parsedRange.lower);
          }
          if (Object.prototype.hasOwnProperty.call(defaultFilters, 'audioDurationUpper')) {
            mergedFilters.audioDurationUpper = String(parsedRange.upper);
          }
        }
      }
    }
  }
  
  if (location.search) {
  }
  
  return {
    filters: mergedFilters,
    advancedFilters: mergedAdvancedFilters,
    customStart,
    customEnd
  };
};

/** Pagination for list views — 1-based, query key defaults to `page` (use `custPage` for customer lists). */
export const getListPageFromLocation = (location, pageParam = 'page') => {
  if (!location?.search) return 1;
  const raw = new URLSearchParams(location.search).get(pageParam);
  const n = parseInt(String(raw || '1'), 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
};

// Update URL with current filters without navigation (optional listPage keeps pagination in sync for back/forward)
export const updateUrlWithFilters = (
  navigate,
  location,
  filters,
  advancedFilters = {},
  customStart = null,
  customEnd = null,
  listPage = null,
  listPageOptions = null
) => {
  const pageParam = listPageOptions?.pageParam || 'page';
  let queryString = serializeFiltersToQuery(filters, advancedFilters, customStart, customEnd);
  const params = new URLSearchParams(queryString);
  if (listPage !== null && listPage !== undefined) {
    if (Number(listPage) > 1) {
      params.set(pageParam, String(listPage));
    } else {
      params.delete(pageParam);
    }
    // Keep conversation vs customer list pagination independent when syncing list URLs
    if (pageParam === 'page') {
      params.delete('custPage');
    } else if (pageParam === 'custPage') {
      params.delete('page');
    }
  }
  queryString = params.toString();
  const newUrl = `${location.pathname}${queryString ? '?' + queryString : ''}`;

  // Use replace to avoid adding to browser history for every filter change
  navigate(newUrl, { replace: true });
};

// Navigate to a different page while preserving current filters
export const navigateWithFilters = (navigate, targetPath, filters, advancedFilters = {}, customStart = null, customEnd = null) => {
  const queryString = serializeFiltersToQuery(filters, advancedFilters, customStart, customEnd);
  const newUrl = `${targetPath}${queryString ? '?' + queryString : ''}`;
  navigate(newUrl);
};
